// Patient queries. `use<X>()` = declarative subscription, `useFetch<X>()` = imperative one-shot
// (event handlers). Same queryKey across both → shared cache.

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import PatientService from '@/services/PatientService';
import type {
  PatientDetails,
  MeasureSummary,
  MeasureReadingsResult,
  ProgramNoteListResult,
} from '@/services/PatientService';
import { isArchivedInProgram } from '@/logic/withdrawalRequest';
import { PATIENT_KEYS } from './keys';

// Full roster — cross-component patient name + phone lookups.
export function usePatients() {
  return useQuery({
    queryKey: [...PATIENT_KEYS.all, 'roster'] as const,
    queryFn: () => PatientService.listPatients(),
    staleTime: 60_000,
  });
}

export function usePatient(userid: string | number | null | undefined) {
  return useQuery({
    queryKey: PATIENT_KEYS.byId(userid ?? ''),
    queryFn: () => PatientService.getPatient(userid!),
    enabled: userid != null && userid !== '',
  });
}

// Whether the patient is archived on the Withings program (null = not read). Same key as usePatient.
export function usePatientArchived(userid: string | number | null | undefined) {
  return useQuery({
    queryKey: PATIENT_KEYS.byId(userid ?? ''),
    queryFn: () => PatientService.getPatient(userid!),
    select: (p: PatientDetails) => isArchivedInProgram(p),
    enabled: userid != null && userid !== '',
    // The window compares it with a request read at staleTime 0; the patient file's own copy may be minutes old.
    staleTime: 0,
  });
}

// Authoritative patients-table demographics (Platform API CRUD) — the source
// of truth for the CMS $align payload.
export function usePatientRecord(userid: string | number | null | undefined) {
  return useQuery({
    queryKey: PATIENT_KEYS.record(userid ?? ''),
    queryFn: () => PatientService.getPatientRecord(userid!),
    enabled: userid != null && userid !== '',
  });
}

export function useFetchPatient(): (userid: string | number) => Promise<PatientDetails> {
  const qc = useQueryClient();
  return useCallback(
    (userid: string | number) => qc.fetchQuery({
      queryKey: PATIENT_KEYS.byId(userid),
      queryFn: () => PatientService.getPatient(userid),
    }),
    [qc],
  );
}

export function useFetchMeasures(): (
  userid: string | number,
  options?: { days?: number }
) => Promise<MeasureSummary> {
  const qc = useQueryClient();
  return useCallback(
    (userid, options = {}) => qc.fetchQuery({
      queryKey: PATIENT_KEYS.measures(userid, options.days),
      queryFn: () => PatientService.getMeasuresByUser(userid, options),
    }),
    [qc],
  );
}

export function useMeasureReadings(
  userid: string | number | null | undefined,
  meastypes: string,
  options: { startdateymd?: string; enddateymd?: string; limit?: number; offset?: number } = {},
) {
  return useQuery({
    queryKey: [...PATIENT_KEYS.measureReadings(userid ?? '', meastypes), options],
    queryFn: () => PatientService.getMeasureReadings(userid!, meastypes, options),
    enabled: userid != null && userid !== '' && meastypes !== '',
  });
}

// Devices attached to the patient's Withings account (auto-attached on setup).
export function usePatientDevices(userid: string | number | null | undefined) {
  return useQuery({
    queryKey: PATIENT_KEYS.devices(userid ?? ''),
    queryFn: () => PatientService.getDevicesByPatient(userid!),
    enabled: userid != null && userid !== '',
    staleTime: 60_000,
  });
}

// Program notes — newest-first list for a patient.
export function usePatientNotes(userid: string | number | null | undefined) {
  return useQuery({
    queryKey: PATIENT_KEYS.notes(userid ?? ''),
    queryFn: () => PatientService.listNotes(userid!),
    enabled: userid != null && userid !== '',
  });
}

export type {
  PatientDetails,
  MeasureSummary,
  MeasureReadingsResult,
  ProgramNoteListResult,
};
